import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { MdWidth } from '@components/Responsive';
import { useSelectedMVComment, useAssetMVPermissions } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useResponsive } from '@hooks/useResponsive';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import ControlBar from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/ControlBar';
import ShapesDrawingTools from '@pages/MediaViewer/AssetViewer/ShapesDrawingTools';
import FloatCommentEditor from '@pages/MediaViewer/components/FloatCommentEditor';
import FloatCommentItem from '@pages/MediaViewer/components/FloatCommentItem';
import { commentHasFloatAnchor } from '@pages/MediaViewer/components/FloatCommentItem/floatCommentHelpers';
import { AssetItemDto, AssetVersionItemDto } from '@api/Api';
import { useApprovalPassword } from '@context/ApprovalPasswordContext';
import DropdownDecisionsAssetMobile from '@components/MediaViewerHeader/components/DropdownDecisions/DropdownDecisionsAssetMobile';
import useOnboardingProcess from '@hooks/useOnboardingProcess';

import './MediaViewerMobileAction.less';

export type MediaViewerMobileActionVisibility = {
  showSidebar: boolean;
  showShapesToolbar: boolean;
  showShapesCommentsToggle: boolean;
  showPanelAction: boolean;
  showControlBar: boolean;
  showFloatCommentEditor: boolean;
  showFloatCommentItem: boolean;
  showMobileDecision: boolean;
};

export function useMediaViewerMobileActionVisibility({
  isShowFloatCommentForm,
  isShowFloatCommentItem
}: {
  isShowFloatCommentForm: boolean;
  isShowFloatCommentItem: boolean;
}): MediaViewerMobileActionVisibility | null {
  const { isMdWidth } = useResponsive();
  const {
    selectedCommentId,
    selectedVersion,
    shapesDrawingTools,
    isMobileExpanded,
    isHideComments,
    activePageNumber,
    documentHighlight,
    isMobileSidebarCommentEditing
  } = useAssetViewerState();
  const selectedComment = useSelectedMVComment();
  const hasAnnotations = useHasAnnotations();

  return useMemo(() => {
    if (!isMdWidth) return null;

    const assetType = selectedVersion?.metadata.type;
    const isDocument =
      assetType === 'document' ||
      assetType === 'presentation' ||
      assetType === 'spreadsheet';

    const hasDocumentHighlight = !!documentHighlight?.rects?.length;

    const isSidebarCommentDraft =
      (selectedCommentId === 'reply' && !selectedComment) ||
      isMobileSidebarCommentEditing;

    const isFloatCommentEditorActive =
      !isSidebarCommentDraft &&
      isShowFloatCommentForm &&
      selectedCommentId === 'new' &&
      (shapesDrawingTools.shape !== 'cursor' || hasDocumentHighlight);

    const isFloatCommentEditorUiExpanded =
      isMobileExpanded || hasDocumentHighlight || hasAnnotations;

    const isFloatCommentEditorToolsActive =
      isFloatCommentEditorActive && isFloatCommentEditorUiExpanded;

    const isCommentOnActivePage =
      !isDocument ||
      selectedComment?.documentPage == null ||
      selectedComment.documentPage === activePageNumber;

    const isTextHighlightMode =
      isDocument && shapesDrawingTools.shape === 'documentHighlight';

    const isFloatCommentItemActive =
      !isSidebarCommentDraft &&
      isShowFloatCommentItem &&
      !isHideComments &&
      !!selectedCommentId &&
      selectedCommentId !== 'new' &&
      !!selectedComment &&
      commentHasFloatAnchor(selectedComment) &&
      isCommentOnActivePage;

    return {
      showFloatCommentEditor:
        isFloatCommentEditorActive && !isTextHighlightMode,
      showFloatCommentItem: isFloatCommentItemActive && !isTextHighlightMode,
      showSidebar: !isFloatCommentItemActive && !isFloatCommentEditorActive,
      showShapesCommentsToggle:
        !isFloatCommentItemActive &&
        !isFloatCommentEditorActive &&
        !isTextHighlightMode,
      showShapesToolbar:
        !isFloatCommentItemActive &&
        !isFloatCommentEditorToolsActive &&
        !isTextHighlightMode,
      showPanelAction: !isFloatCommentItemActive,
      showControlBar:
        !isFloatCommentItemActive &&
        !isFloatCommentEditorToolsActive &&
        !isTextHighlightMode,
      showMobileDecision:
        !isFloatCommentItemActive &&
        !isFloatCommentEditorActive &&
        !isTextHighlightMode &&
        shapesDrawingTools.shape === 'cursor'
    };
  }, [
    isMdWidth,
    isShowFloatCommentForm,
    isShowFloatCommentItem,
    selectedCommentId,
    isMobileSidebarCommentEditing,
    shapesDrawingTools.shape,
    isMobileExpanded,
    hasAnnotations,
    isHideComments,
    activePageNumber,
    selectedComment,
    selectedVersion?.metadata.type,
    documentHighlight?.text,
    documentHighlight?.rects?.length
  ]);
}

type MediaViewerMobileActionProps = {
  visibility: MediaViewerMobileActionVisibility;
  asset: AssetItemDto;
  selectedVersion: AssetVersionItemDto;
  commentMentions: boolean;
  canCreateTask: boolean;
  isShowFloatCommentForm: boolean;
  isShowFloatCommentItem: boolean;
  isContainerExpand?: boolean;
  isShowShapeDrawingTools?: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
};

function MediaViewerMobileAction({
  visibility,
  asset,
  selectedVersion,
  commentMentions,
  canCreateTask,
  isShowFloatCommentForm,
  isShowFloatCommentItem,
  isContainerExpand = false,
  isShowShapeDrawingTools = true,
  setSelectedCollapse
}: MediaViewerMobileActionProps) {
  const canLeaveComment = useAssetMVPermissions('comment');
  const { password } = useApprovalPassword();
  const { isMdWidth } = useResponsive();
  const {
    getPlaybackPlayer,
    documentHighlight,
    selectedCommentId,
    setDocumentHighlight,
    setDrawToolsShape
  } = useAssetViewerState();
  const player = getPlaybackPlayer();
  const { tourData, goToStep } = useOnboardingProcess();
  const isAnnouncementMediaViewerMobileTour =
    tourData?.type === 'announcement-media-viewer-mobile';
  const [isShowVideo, setIsShowVideo] = useState<boolean>(false);
  const prevShowSidebarRef = useRef(visibility.showSidebar);

  const assetType = selectedVersion?.metadata.type;
  const isVideo = assetType === 'video';
  const isAudio = assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';

  const shapesClassName = classNames(
    isVideo && 'b-media_viewer_video',
    isAudio && 'b-media_viewer_audio',
    isDocument && 'b-media_viewer_document',
    isContainerExpand && 'b-media_viewer_asset-expanded'
  );
  const showShapesDrawingTools =
    canLeaveComment &&
    isShowShapeDrawingTools &&
    (visibility.showShapesToolbar || visibility.showShapesCommentsToggle);
  const showMobileActionButtons =
    showShapesDrawingTools ||
    visibility.showMobileDecision ||
    isAnnouncementMediaViewerMobileTour;

  useEffect(() => {
    return () => {
      setIsShowVideo(false);
    };
  }, [visibility.showFloatCommentItem]);

  useEffect(() => {
    const wasSidebarVisible = prevShowSidebarRef.current;
    prevShowSidebarRef.current = visibility.showSidebar;

    if (!isMdWidth) return;
    if (!visibility.showSidebar) return;
    if (wasSidebarVisible) return;
    if (selectedCommentId === 'new' || selectedCommentId === 'reply') return;

    document.getSelection()?.removeAllRanges();
    setDocumentHighlight(null);
    setDrawToolsShape('cursor');
  }, [
    isMdWidth,
    visibility.showSidebar,
    selectedCommentId,
    documentHighlight?.text,
    documentHighlight?.rects?.length,
    setDocumentHighlight,
    setDrawToolsShape
  ]);

  const controlBar = useMemo(
    () =>
      player && (isVideo || isAudio)
        ? createPortal(
            <ControlBar
              player={player}
              asset={selectedVersion}
              setSelectedCollapse={setSelectedCollapse}
              className={classNames(
                isShowVideo && 'video_control_bar_mobile-show-video'
              )}
            />,
            player.el()
          )
        : null,
    [
      player,
      isVideo,
      isAudio,
      selectedVersion,
      setSelectedCollapse,
      isShowVideo
    ]
  );

  return (
    <MdWidth>
      <div
        className={classNames('media_viewer_mobile_action', {
          'media_viewer_mobile_action-show-video': isShowVideo,
          'tour-announcement-media-viewer-mobile-action-wrap':
            isAnnouncementMediaViewerMobileTour,
          'tour-announcement-media-viewer-mobile-action-active':
            isAnnouncementMediaViewerMobileTour && goToStep === 2
        })}
      >
        {showMobileActionButtons && (
          <div
            className={classNames(
              'media_viewer_mobile_action-buttons',
              'tour-announcement-media-viewer-mobile-action',
              shapesClassName
            )}
          >
            {showShapesDrawingTools && (
              <ShapesDrawingTools
                player={getPlaybackPlayer()}
                className={shapesClassName}
              />
            )}
            {(visibility.showMobileDecision ||
              isAnnouncementMediaViewerMobileTour) && (
              <DropdownDecisionsAssetMobile
                password={password}
                className={shapesClassName}
              />
            )}
          </div>
        )}
        {isShowFloatCommentForm && visibility.showFloatCommentEditor && (
          <FloatCommentEditor
            asset={asset}
            canCreateTask={canCreateTask}
            commentMentions={commentMentions}
            selectedVersion={selectedVersion}
          />
        )}
        {isShowFloatCommentItem && visibility.showFloatCommentItem && (
          <FloatCommentItem
            asset={asset}
            selectedVersion={selectedVersion}
            commentMentions={commentMentions}
            canCreateTask={canCreateTask}
            isShowVideo={isShowVideo}
            onExpandVideo={(value) => setIsShowVideo(value)}
          />
        )}
        {(visibility.showControlBar || isShowVideo) && controlBar}
      </div>
    </MdWidth>
  );
}

export default MediaViewerMobileAction;
